import { useTranslation } from "@pancakeswap/localization"
import { Currency } from "@pancakeswap/swap-sdk-core"
import { Balance, Box, Button, Flex, FlexGap, InjectedModalProps, Modal, SyncAltIcon, Text, useModal } from "@pancakeswap/uikit"
import { nonfungiblePositionManagerABI, Pool, TICK_SPACINGS, TickMath, tickToPrice } from "@pancakeswap/v3-sdk"
import ConnectWalletButton from "components/ConnectWalletButton"
import { CurrencyLogo } from "components/Logo"
import TransactionConfirmationModal from "components/TransactionConfirmationModal"
import { PointsFarmingABI } from "config/abi/PointsFarming"
import useActiveWeb3React from "hooks/useActiveWeb3React"
import useCatchTxError from "hooks/useCatchTxError"
import { usePointsFarmingContract, useV3NFTPositionManagerContract } from "hooks/useContract"
import useTransactionDeadline from "hooks/useTransactionDeadline"
import { usePool } from "hooks/v3/usePools"
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import { formatAmount } from "utils/formatCurrencyAmount"
import { encodeFunctionData } from "viem"
import { PositionData } from "views/V3Info/types"
import { PoolData } from "../hooks/usePools"

const PositionContainer = styled(Flex) <{ selected?: boolean }>`
  flex-direction: column;
  gap: 12px;
  background: ${({ selected, theme }) => selected ? theme.colors.primary : '#2B2634'};
  border-radius: 16px;
  padding: 16px 20px;
  
  @media screen and (max-width: 480px) {
    gap: 4px;
  }
`

const SliderRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  
  @media screen and (max-width: 480px) {
    gap: 8px;
  }
`

const ButtonGrid = styled(Flex)`
  flex-wrap: wrap;
  justify-content: space-evenly;
  gap: 4px;
  background: #231F2B;
  padding: 4px;
  border-radius: 12px;
  
  @media screen and (max-width: 480px) {
    gap: 4px;
  }
`

const PercentageButton = styled(Button).attrs({ scale: "sm" }) <{ selected: boolean }>`
  font-size: 15px;
  flex: 1;
  padding: 8px;
  height: 38px;
  border-radius: 8px;
  background: ${({ selected }) => selected ? '#49415A' : 'transparent'};
  box-shadow: none;
`

const Hr = styled.hr`
  width: 100%;
  border-color: #FFFFFF17;
  border-width: 1px 0 0 0;
  margin: 1em 0;
`

const TokenId = styled(Text)`
  background: #3E3A46;
  border-radius: 16px;
  padding: 2px 1em;
`

const InRange = styled(Text) <{ valid: boolean }>`
  border-radius: 8px;
  padding: 5px 5px;
  font-size: 12px;
  font-weight: bold;
  line-height: 12px;
  white-space: nowrap;
  background: ${({ theme, valid }) => valid ? '#2EBF43' : theme.colors.textSubtle};
  color: ${({ theme }) => theme.colors.backgroundAlt};
`

interface StakeModalProps extends InjectedModalProps {
  currencyA: Currency
  currencyB: Currency
  positions: PositionData[]
  poolData: PoolData
  setUnstakeTxHash?: Dispatch<SetStateAction<string>>
  // positionDetails: PositionDetails
  // poolStats: PoolData
  // liquidityUSD: number
  // currentPrice: number
  // token0Percentage: number
  // token1Percentage: number
}

const isFullrange = (position: PositionData, tickSpacing: number) => {
  if (!tickSpacing)
    return true
  const tickLower = Math.round(TickMath.MIN_TICK / tickSpacing) * tickSpacing
  const tickUpper = Math.floor(TickMath.MAX_TICK / tickSpacing) * tickSpacing
  return tickLower === position.tickLower && tickUpper === position.tickUpper
}

const PositionSelect = ({ currencyA, currencyB, position, pool, updateResult }: { currencyA: Currency, currencyB: Currency, position: PositionData, pool: Pool, updateResult: (tokenId: string, selected: boolean) => void }) => {
  const [selected, setSelect] = useState(true)
  const [inverted, setInvert] = useState(false)

  const [currency0, currency1] = inverted ? [currencyB, currencyA] : [currencyA, currencyB]
  const priceLower = tickToPrice(currency0.wrapped, currency1.wrapped, inverted ? position.tickUpper : position.tickLower)
  const priceUpper = tickToPrice(currency0.wrapped, currency1.wrapped, inverted ? position.tickLower : position.tickUpper)

  const fullRange = isFullrange(position, TICK_SPACINGS[pool?.fee])
  const inRange = position.tick >= position.tickLower && position.tick <= position.tickUpper

  return (
    <PositionContainer width={['100%', , '500px']} selected={selected} onClick={() => {
      setSelect(!selected)
      updateResult(String(position.tokenId), !selected)
    }}>
      <FlexGap gap="8px" alignItems="flex-end">
        <TokenId fontSize={14}>Position #{position.tokenId.toString()}</TokenId>
        <InRange valid={inRange}>{inRange ? 'In Range' : 'Out of Range'}</InRange>
      </FlexGap>
      <FlexGap gap="8px" alignItems="baseline" pl={2} mt={1}>
        <Text fontSize={[12, 16]} bold>{fullRange ? '0' : priceLower.toSignificant(8)} - {fullRange ? '∞' : priceUpper.toSignificant(8)}</Text>
        <Text fontSize={[12, 14]} color="textSubtle">{currency1.symbol} per {currency0.symbol}</Text>
        <SyncAltIcon color="textSubtle" onClick={() => setInvert(!inverted)} />
      </FlexGap>
    </PositionContainer>
  )
}

const UnstakeModal: React.FC<StakeModalProps> = ({
  currencyA,
  currencyB,
  positions,
  poolData,
  onDismiss,
  setUnstakeTxHash
}) => {
  const { t } = useTranslation()

  const { account } = useActiveWeb3React()

  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [selections, setSelections] = useState<{ [tokenId: string]: boolean }>({})
  const positionManager = useV3NFTPositionManagerContract()

  const [, pool] = usePool(currencyA, currencyB, poolData.feeTier * 10000)

  const { token0Stake, token1Stake } = useMemo(() => {
    return positions.reduce((summary, position) => {
      const selected = selections[String(position.tokenId)]
      if (selected) {
        if (!pool)
          return {
            token0Stake: summary.token0Stake + position.liquidityToken0,
            token1Stake: summary.token1Stake + position.liquidityToken1,
          }
        return {
          token0Stake: summary.token0Stake + position.liquidityToken0,
          token1Stake: summary.token1Stake + position.liquidityToken1,
          // token0Stake: summary.token0Stake + poolData.reserve0 * Number(position.liquidity) / Number(pool.liquidity),
          // token1Stake: summary.token1Stake + poolData.reserve1 * Number(position.liquidity) / Number(pool.liquidity),
        }
      }
      return summary
    }, {
      token0Stake: 0,
      token1Stake: 0,
    })
  }, [pool, poolData, positions, selections])

  const hasSelection = Object.values(selections).some(Boolean)

  const deadline = useTransactionDeadline()

  const { fetchTxResponse } = useCatchTxError()

  const handleDismiss = useCallback(() => {
    onDismiss?.()
  }, [onDismiss])

  const translationData = useMemo(
    () => ({
      baseAmount: formatAmount(token0Stake),
      baseSymbol: currencyA.symbol,
      quoteAmount: formatAmount(token1Stake),
      quoteSymbol: currencyB.symbol,
    }),
    [currencyA, currencyB, token0Stake, token1Stake],
  )

  const pendingText = useMemo(
    () =>
      t('Unstaking %baseAmount% %baseSymbol% %quoteAmount% %quoteSymbol%', translationData),
    [t, translationData],
  )

  const [onPresentConfirmModal] = useModal(
    <TransactionConfirmationModal
      key={`confirm-unstake-${poolData.address}`}
      minWidth={['100%', null, '420px']}
      title={t('Withdraw')}
      // customOnDismiss={}
      attemptingTxn={attemptingTxn}
      hash={''}
      content={() => null}
      pendingText={pendingText}
    />,
    false,
    true,
    `confirm-unstake-${poolData.address}`,
  )

  const handleWithdrawSubmit = async () => {
    onPresentConfirmModal()
    handleWithdraw()
  }

  const handleWithdraw = async () => {
    const options = {}

    setAttemptingTxn(true)

    const calldata = positions
      .map(p => {
        if (!selections[String(p.tokenId)]) {
          return undefined
        }
        return encodeFunctionData({
          abi: nonfungiblePositionManagerABI,
          functionName: 'unstake',
          args: [p.tokenId],
        })
      })
      .filter(Boolean);

    await fetchTxResponse(() =>
      positionManager.write.multicall(
        [calldata],
        { ...options }
      ), {
      summary: "Unstaked",
      translatableSummary: {
        text: 'Unstaked %baseAmount% %baseSymbol% and %quoteAmount% %quoteSymbol%',
        data: translationData,
      },
      type: 'unstake-v3',
    }).then((tx) => {
      setUnstakeTxHash?.(tx.hash)
    }
    ).catch((err) => {
      console.log('error', err)
    }).then(() => onDismiss?.()).finally(() => setAttemptingTxn(false))
  }

  useEffect(() => {
    positions.forEach(position => {
      if (selections[String(position.tokenId)]) {
        setSelections((p) => ({ ...p, [String(position.tokenId)]: false }))
      } else {
        setSelections((p) => ({ ...p, [String(position.tokenId)]: true }))
      }
    })
  }, [positions])

  return (
    <Modal title={t("Unstake Position")} onDismiss={handleDismiss}>
      <FlexGap gap="8px" flexDirection="column">
        {
          positions.map(position =>
            <PositionSelect
              key={`position-${position.tokenId}`}
              currencyA={currencyA}
              currencyB={currencyB}
              position={position}
              pool={pool}
              updateResult={(tokenId, selected) => {
                setSelections((p) => ({ ...p, [tokenId]: selected }))
              }} />
          )
        }
      </FlexGap>
      <FlexGap flexDirection="column" gap="8px" mt={4}>
        <Text fontSize={15}>
          {t('Unstake')}
        </Text>
        <FlexGap gap="8px">
          <CurrencyLogo currency={currencyA} size="17px" />
          <Text color="textSubtle" fontSize={12}>
            {currencyA?.symbol}
          </Text>
          <Balance value={Number(token0Stake || '0')} decimals={4} fontSize={12} ml="auto" />
        </FlexGap>
        <FlexGap gap="8px">
          <CurrencyLogo currency={currencyB} size="17px" />
          <Text color="textSubtle" fontSize={12}>
            {currencyB?.symbol}
          </Text>
          <Balance value={Number(token1Stake || '0')} decimals={4} fontSize={12} ml="auto" />
        </FlexGap>
      </FlexGap>
      {
        !!pool &&
        <>
          <Hr />
          <FlexGap flexDirection="column" gap="8px">
            <Text fontSize={15}>
              {t('Prices')}
            </Text>
            <FlexGap gap="8px">
              <Text color="textSubtle" fontSize={12}>
                1 {currencyB?.symbol} =
              </Text>
              <Text fontSize={12} ml="auto">
                {formatAmount(pool.priceOf(currencyB.wrapped).toSignificant(6))} {currencyA?.symbol}
              </Text>
            </FlexGap>
            <FlexGap gap="8px">
              <Text color="textSubtle" fontSize={12}>
                1 {currencyA?.symbol} =
              </Text>
              <Text fontSize={12} ml="auto">
                {formatAmount(pool.priceOf(currencyA.wrapped).toSignificant(6))} {currencyB?.symbol}
              </Text>
            </FlexGap>
          </FlexGap>
        </>
      }
      <Box mt="16px">
        {!account ? (
          <ConnectWalletButton width="100%" style={{ minHeight: '72px' }} />
        ) : (
          <Button
            onClick={handleWithdrawSubmit}
            width="100%"
            style={{
              minHeight: '72px',
              background: hasSelection ? '#F66' : '#3F384C'
            }}
            disabled={!hasSelection}
          >
            {t('Unstake')}
          </Button>
        )}
      </Box>
    </Modal>
  )
}

export default UnstakeModal
